
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/sentry';

/**
 * Generates a PDF server-side via the Browserless pipeline and triggers
 * a blob download — no popup window required, works on mobile.
 */
export async function downloadPdfFromHtml(
  htmlContent: string,
  filename: string
): Promise<boolean> {
  console.log('[downloadPdfFromHtml] Calling generate-pdf-puppeteer edge function...');

  const { data, error } = await supabase.functions.invoke('generate-pdf-puppeteer', {
    body: { htmlContent },
  });

  if (error) {
    console.error('[downloadPdfFromHtml] Edge function error:', error);
    captureError(error, { feature: 'pdf-generation', action: 'download-pdf-from-html' });
    throw new Error(error.message || 'PDF generation failed');
  }

  if (!data?.pdfBase64) {
    throw new Error('No PDF data returned from server');
  }

  console.log('[downloadPdfFromHtml] PDF received, base64 length:', data.pdfBase64.length);

  // Decode base64 → Uint8Array → Blob
  const raw = atob(data.pdfBase64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });

  // Programmatic <a download> click — mobile-safe, no popup
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  console.log('[downloadPdfFromHtml] Download triggered for', a.download);
  return true;
}
